import { DatePicker } from '@cambridgeassessment/cambridge-ui';
import { ComponentSeries, convertToMidnight, INTERNAL_ROLES, Role, Roles } from '@cambridgeassessment/lib-ee-commons';
import { Authorized } from 'lib-browser';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { DATE_DISPLAY_FORMAT } from '../../../../constants';
import { StyledDatePickerWrapper, StyledFieldDescription } from '../Details/styles';

interface SamplingSeriesDate {
  series: ComponentSeries;
  disabled: boolean;
  role: Role | null;
  onSalgoStartDateUpdate: (seriesId: ComponentSeries['id'], startDate: string) => void;
}

const SamplingSeriesFC = (props: SamplingSeriesDate): JSX.Element | null => {
  const [startDate, setStartDate] = useState<string>();
  const minDate = convertToMidnight(props.series.marksSubmissionWindow?.start);
  const maxDate = moment(props.series.marksSubmissionWindow?.buffer).subtract(1, 'd');
  const renderHelperText = useMemo(
    (): string =>
      props.disabled
        ? 'No sampling algorithm has been selected.'
        : 'The date must be a date before the Buffer Date for that series',
    [props.disabled],
  );

  const changeStartDate = (date: moment.Moment) => {
    setStartDate(date.toISOString());
    props.onSalgoStartDateUpdate(props.series.id, date.toISOString());
  };

  useEffect(() => {
    setStartDate(props.series.salgoStartDate);
  }, [props.series.salgoStartDate]);

  if (!props.series.marksSubmissionWindow) {
    return null;
  }

  const rolesExcludingAdministrator = INTERNAL_ROLES.filter(
    (role) => ![Roles.SUBMIT_ADMINISTRATOR, Roles.SUBMIT_SUPER_ADMIN].includes(role),
  );

  return (
    <>
      <StyledFieldDescription data-qa-id={`sampling-series-name-${props.series.name}`}>
        Series name: <b>{props.series.name}</b>
      </StyledFieldDescription>
      <StyledFieldDescription data-qa-id={`sampling-series-sw-${props.series.name}`}>
        Submission window name: <b>{props.series.marksSubmissionWindow.name}</b>
      </StyledFieldDescription>
      <StyledDatePickerWrapper>
        <Authorized
          roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR]}
          readOnlyRoles={rolesExcludingAdministrator}
          selectedRole={props.role}
          hideOnDenied
        >
          <DatePicker
            key={startDate}
            value={startDate}
            onChange={changeStartDate}
            autoOk
            disabled={props.disabled}
            format={DATE_DISPLAY_FORMAT}
            minDate={minDate}
            maxDate={maxDate}
            dataQaId={`sampling-start-date-selector-${props.series.name}`}
            helperText={renderHelperText}
          />
        </Authorized>
      </StyledDatePickerWrapper>
    </>
  );
};

export default SamplingSeriesFC;
